import React, { useRef, useCallback } from 'react';
import { Box, TextField } from '@mui/material';

interface OtpInputProps {
  length?: number;
  value: string;
  onChange: (value: string) => void;
  error?: boolean;
  disabled?: boolean;
}

const OtpInput: React.FC<OtpInputProps> = ({
  length = 6,
  value,
  onChange,
  error = false,
  disabled = false,
}) => {
  const inputsRef = useRef<(HTMLInputElement | null)[]>([]);

  const focusInput = useCallback((index: number) => {
    if (index >= 0 && index < length) {
      inputsRef.current[index]?.focus();
    }
  }, [length]);

  const handleChange = useCallback(
    (index: number, char: string) => {
      if (char && !/^\d$/.test(char)) return;

      const arr = value.split('');
      arr[index] = char;
      const newValue = arr.join('').slice(0, length);
      onChange(newValue);

      if (char && index < length - 1) {
        focusInput(index + 1);
      }
    },
    [value, length, onChange, focusInput],
  );

  const handleKeyDown = useCallback(
    (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Backspace') {
        e.preventDefault();
        const arr = value.split('');
        if (arr[index]) {
          arr[index] = '';
          onChange(arr.join(''));
        } else if (index > 0) {
          arr[index - 1] = '';
          onChange(arr.join(''));
          focusInput(index - 1);
        }
      } else if (e.key === 'ArrowLeft') {
        focusInput(index - 1);
      } else if (e.key === 'ArrowRight') {
        focusInput(index + 1);
      }
    },
    [value, onChange, focusInput],
  );

  const handlePaste = useCallback(
    (e: React.ClipboardEvent) => {
      e.preventDefault();
      const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, length);
      if (pasted) {
        onChange(pasted);
        focusInput(Math.min(pasted.length, length - 1));
      }
    },
    [length, onChange, focusInput],
  );

  return (
    <Box sx={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
      {Array.from({ length }).map((_, i) => (
        <TextField
          key={i}
          inputRef={(el) => { inputsRef.current[i] = el; }}
          value={value[i] || ''}
          onChange={(e) => handleChange(i, e.target.value.slice(-1))}
          onKeyDown={(e) => handleKeyDown(i, e as React.KeyboardEvent<HTMLInputElement>)}
          onPaste={handlePaste}
          disabled={disabled}
          error={error}
          inputProps={{
            maxLength: 1,
            style: {
              textAlign: 'center',
              fontSize: 24,
              fontFamily: 'monospace',
              padding: '12px 0',
              color: '#fff',
            },
            inputMode: 'numeric',
          }}
          sx={{
            width: 48,
            '& .MuiOutlinedInput-root': {
              height: 56,
              '& fieldset': {
                borderColor: error ? '#f44336' : 'rgba(255,255,255,0.2)',
              },
              '&:hover fieldset': {
                borderColor: error ? '#f44336' : 'rgba(255,255,255,0.4)',
              },
              '&.Mui-focused fieldset': {
                borderColor: error ? '#f44336' : 'primary.main',
              },
            },
          }}
        />
      ))}
    </Box>
  );
};

export default OtpInput;
